import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, FileText, Crown, Plus } from 'lucide-react';
import { OutputEstimator } from '../components/OutputEstimator';
const fmt = (n: number) => n.toLocaleString('en-US');
export function ComparePage() {
  const [adSpend, setAdSpend] = useState(5000);
  const [cpaLow, setCpaLow] = useState(200);
  const [cpaHigh, setCpaHigh] = useState(500);
  const build = 4500;
  const platform = 1500;
  const adSpendChunks = Math.max(1, Math.ceil(adSpend / 2500));
  const scopeMultiplier = adSpendChunks;
  const campaign = 1000 * adSpendChunks;
  const recruiter = 1500 * adSpendChunks;
  return <div className="min-h-screen w-full bg-[#0A0F1C] text-slate-200 selection:bg-[#7FE0C3]/30 font-sans pb-24" data-id="element-852" data-mp="3qvznq2670ia3">
      {/* 1. HERO */}
      <section className="relative pt-32 pb-16 px-6 md:px-12 max-w-5xl mx-auto text-center" data-id="element-853" data-mp="164h4kgicmock">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-[#7FE0C3]/5 blur-[120px] rounded-full pointer-events-none" data-id="element-854" data-mp="115e65ewvjgdk" />

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} data-id="element-855" data-mp="34rf5xsq3urph">
          <div className="inline-block px-3 py-1 rounded-full bg-slate-800 border border-slate-700 text-slate-400 text-xs font-bold uppercase tracking-widest mb-6" data-id="element-856" data-mp="3drc029v9kirx">
            Your Partnership
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight" data-id="element-857" data-mp="2q3kbo7wg8faa">
            The Investment, Laid Out
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed" data-id="element-858" data-mp="2v1m1rcsalev9">
            Everything we discussed in one place, so you can review the numbers
            and share them with your team.
          </p>
        </motion.div>
      </section>

      {/* 2. ADVANTAGE */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto mb-8" data-id="element-859" data-mp="27cb0lu715igl">
        <OutputEstimator adSpend={adSpend} cpaLow={cpaLow} cpaHigh={cpaHigh} onAdSpendChange={setAdSpend} onCpaLowChange={setCpaLow} onCpaHighChange={setCpaHigh} data-id="element-860" data-mp="1zcddkzvyiwd3" />

        <div className="bg-slate-900/60 border border-[#7FE0C3]/40 rounded-2xl p-8 relative" data-id="element-861" data-mp="292g5ewrce49u">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-1 bg-[#7FE0C3] text-[#163A5F] text-xs font-bold uppercase tracking-widest rounded-full whitespace-nowrap" data-id="element-862" data-mp="242tqmivv8fyx">
            Flagship Partnership
          </div>

          <div className="flex items-center space-x-3 mb-3 mt-2" data-id="element-863" data-mp="1ce0diu0rfitk">
            <div className="p-2 bg-[#7FE0C3]/10 border border-[#7FE0C3]/20 rounded-lg" data-id="element-864" data-mp="1qcqdqiivlym5">
              <FileText className="w-6 h-6 text-[#7FE0C3]" data-id="element-865" data-mp="1lcc9zcob5atx" />
            </div>
            <h2 className="text-2xl font-bold text-white" data-id="element-866" data-mp="31qlqjkmb5va7">Advantage</h2>
          </div>
          <p className="text-slate-400 text-base mb-8 leading-relaxed max-w-2xl" data-id="element-867" data-mp="2wrr7qiocc6q3">
            We build and own the recruiting system, and your team runs the
            interviews. A predictable hiring engine with full accountability.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10" data-id="element-868" data-mp="njvvyyb6t11i">
            {/* Pricing */}
            <div data-id="element-869" data-mp="9kmrux099xdw">
              <div className="flex items-end justify-between mb-5" data-id="element-870" data-mp="9l601m03t7eq">
                <span className="text-xs text-[#7FE0C3] uppercase tracking-wider" data-id="element-871" data-mp="ejfvzytnkc1e">
                  Build (One-Time)
                </span>
                <span className="text-2xl font-bold text-white" data-id="element-872" data-mp="12a3grkoo2w5d">
                  ${fmt(build)}
                </span>
              </div>
              <p className="-mt-3 mb-5 text-right text-[11px] text-slate-500" data-id="element-873" data-mp="sipdr2xibe7h">
                Add $1,500 if a corporate recruiter is included.
              </p>
              <div className="flex items-end justify-between" data-id="element-874" data-mp="xf629y8mbul3">
                <span className="text-xs text-[#7FE0C3] uppercase tracking-wider" data-id="element-875" data-mp="1l630y9bekvee">
                  Monthly Service
                </span>
                <span className="text-2xl font-bold text-white" data-id="element-876" data-mp="1pzec2mn8zyc4">
                  ${fmt(platform + campaign)}
                </span>
              </div>
              <div className="mt-3 rounded-lg bg-slate-950/40 border border-slate-800/60 px-4 py-3" data-id="element-877" data-mp="3sh0d0vzees3e">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2" data-id="element-878" data-mp="3oh9c3zg5qvl5">
                  What makes up the monthly
                </div>
                <ul className="space-y-1 text-xs text-slate-400" data-id="element-879" data-mp="k28dt3coezci">
                  <li className="flex justify-between" data-id="element-880" data-mp="k1giltmzr3mr">
                    <span data-id="element-881" data-mp="g0ek5f0wevlu">Catalyst platform</span>
                    <span data-id="element-882" data-mp="g2r8c9x9227c">${fmt(platform)}</span>
                  </li>
                  <li className="flex justify-between" data-id="element-883" data-mp="b8mzfl4fbk3m">
                    <span data-id="element-884" data-mp="6b3anra3noxf">Pipeline management ({scopeMultiplier}x)</span>
                    <span data-id="element-885" data-mp="690pbkjw6jz1">${fmt(campaign)}</span>
                  </li>
                  <li className="flex justify-between text-slate-500" data-id="element-886" data-mp="13rschkz1ur6t">
                    <span data-id="element-887" data-mp="yz8wkhcw3jyu">
                      Corporate recruiter ({scopeMultiplier}x, optional)
                    </span>
                    <span data-id="element-888" data-mp="16c861f4sqmsj">+${fmt(recruiter)}</span>
                  </li>
                </ul>
                <p className="mt-3 pt-3 border-t border-slate-800/60 text-[11px] text-slate-500 leading-relaxed" data-id="element-889" data-mp="1bajhfuaqp5cv">
                  Covers up to ${fmt(adSpendChunks * 2500)} per month in managed
                  ad spend.
                </p>
              </div>

              <div className="mt-6 pt-6 border-t border-slate-800/50" data-id="element-890" data-mp="1pa1ailv2j4s1">
                <div className="text-xs font-bold uppercase tracking-widest text-[#7FE0C3] mb-4" data-id="element-891" data-mp="1p7otcax4wyg1">
                  Performance-Based Pricing
                </div>
                <div className="space-y-3" data-id="element-892" data-mp="1z0nm84znzb5g">
                  <div className="flex items-baseline justify-between gap-4" data-id="element-893" data-mp="23yfkrhutdvim">
                    <span className="text-sm text-slate-400" data-id="element-894" data-mp="240i8fv8hvck6">
                      <span className="text-white font-medium" data-id="element-895" data-mp="2812a9c40j4cn">
                        Placement bonus
                      </span>{' '}
                      — day 15 active in the field
                    </span>
                    <span className="text-xl font-bold text-white whitespace-nowrap" data-id="element-896" data-mp="cxdvf9hh2vba">
                      $500
                    </span>
                  </div>
                  <div className="flex items-baseline justify-between gap-4" data-id="element-897" data-mp="mn7524y210cw">
                    <span className="text-sm text-slate-400" data-id="element-898" data-mp="hvxi5c3w4b4p">
                      <span className="text-white font-medium" data-id="element-899" data-mp="humgbhu09dg8">
                        Retention bonuses
                      </span>{' '}
                      — day 45 and day 90 active in the field
                    </span>
                    <span className="text-xl font-bold text-white whitespace-nowrap" data-id="element-900" data-mp="15lji41s36udr">
                      $1,000 ea.
                    </span>
                  </div>
                </div>
                <p className="mt-3 text-xs text-slate-500 leading-relaxed" data-id="element-901" data-mp="10km2nwygoefy">
                  Billed only when a hire is still active in the field. You pay
                  for the reps you retain, instead of resumés or hires.
                </p>
              </div>
            </div>

            {/* Value */}
            <div className="lg:border-l lg:border-slate-800 lg:pl-10" data-id="element-902" data-mp="1muufkmsy4bhq">
              <div className="text-sm font-bold text-white mb-3" data-id="element-903" data-mp="1mu2nvn2gyxwq">
                What you get:
              </div>
              <ul className="space-y-2.5" data-id="element-904" data-mp="191mnku0yddzi">
                {['Custom ATS, landing pages, and full funnel build', 'Active ad management and real-time optimization', 'Live applicant screening, 14 hrs/day, 7 days/week', 'Interview auditing and coaching to improve retention', 'Monthly recruiting review and ongoing consulting', 'Optional corporate recruiter to own the pipeline end to end'].map((item, i) => <li key={i} className="flex items-start text-sm text-slate-300" data-id="element-905" data-mp="2kicnjves99ko" data-mpi={i}>
                    <Check className="w-4 h-4 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-906" data-mp="2ojeocgocltor" />
                    <span data-id="element-907" data-mp="1cab7qthi6if2">{item}</span>
                  </li>)}
              </ul>

              <div className="mt-8 pt-6 border-t border-slate-800/50" data-id="element-908" data-mp="17c9pubvtcp56">
                <p className="text-sm text-slate-400 leading-relaxed" data-id="element-909" data-mp="12k9hbv1ognwz">
                  <span className="text-white font-medium" data-id="element-910" data-mp="xlybrwif0zkk">Best for:</span>{' '}
                  teams that want recruiting to finally be consistent and
                  predictable, while keeping interviews and final decisions
                  in-house.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. LEADERSHIP ADD-ON */}
      <section className="px-6 md:px-12 max-w-5xl mx-auto mb-24" data-id="element-911" data-mp="en3tidjqtgzn">
        <div className="text-center mb-8" data-id="element-912" data-mp="eondof8isc6p">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#7FE0C3]/10 border border-[#7FE0C3]/20 text-[#7FE0C3] text-xs font-bold uppercase tracking-widest mb-4" data-id="element-913" data-mp="2dzy4amexlmxs">
            Optional Add-On
          </div>
          <h2 className="text-3xl font-bold text-white mb-3" data-id="element-914" data-mp="2hh2p3vbujf72">
            Add Leadership to Your Partnership
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed" data-id="element-915" data-mp="2li646fm2l778">
            Leadership is an optional add-on that layers onto Advantage. Run it
            in tandem with your main campaign, or ahead of it to put the right
            manager in place before you scale the team beneath them.
          </p>
        </div>

        {/* Visual: Advantage + Leadership */}
        <div className="flex flex-col sm:flex-row items-stretch justify-center gap-4 mb-10" data-id="element-916" data-mp="22lwgd0ld92a1">
          <div className="flex-1 max-w-xs mx-auto sm:mx-0 bg-slate-900/40 border border-slate-700 rounded-xl p-5 text-center" data-id="element-917" data-mp="27iwnzfxv6d39">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-918" data-mp="2ckc9f93yelm6">
              Core Package
            </div>
            <div className="text-white font-bold" data-id="element-919" data-mp="3j1hc04i9igwq">Advantage</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-920" data-mp="3nwmuetcghdg2">
              Recruits and ramps your reps
            </div>
          </div>
          <div className="flex items-center justify-center text-slate-500" data-id="element-921" data-mp="3dmd5ra6bu013">
            <Plus className="w-6 h-6" data-id="element-922" data-mp="3dkig5ik41jqo" />
          </div>
          <div className="flex-1 max-w-xs mx-auto sm:mx-0 bg-slate-900/40 border border-[#7FE0C3]/30 rounded-xl p-5 text-center" data-id="element-923" data-mp="3ictkcxlvvrp1">
            <div className="text-xs text-[#7FE0C3] uppercase tracking-wider mb-1" data-id="element-924" data-mp="3me5e9fbsiz31">
              Add-On
            </div>
            <div className="text-white font-bold flex items-center justify-center" data-id="element-925" data-mp="2pqh35ha5c6sz">
              <Crown className="w-4 h-4 text-[#7FE0C3] mr-2" data-id="element-926" data-mp="2unr2ppr91yuj" />
              Leadership
            </div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-927" data-mp="2yr6yykzepx6p">
              Recruits the managers who lead them
            </div>
          </div>
        </div>

        <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-8" data-id="element-928" data-mp="dzmx8o3vg97a">
          <div className="flex flex-col md:flex-row gap-10" data-id="element-929" data-mp="ixf18ghsjlru">
            <div className="flex-1" data-id="element-930" data-mp="j016ual4fr7f">
              <div className="text-sm font-bold text-white mb-3" data-id="element-931" data-mp="t2enbtbk62tu">
                What you get:
              </div>
              <ul className="space-y-2.5" data-id="element-932" data-mp="o3tljpgnrb3a">
                {['Targeted outreach to experienced operators and team leaders', 'Interviews handled for you', 'Vetted candidates passed to you for final approval only', 'Performance aligned to the leader and their team output'].map((item, i) => <li key={i} className="flex items-start text-sm text-slate-300" data-id="element-933" data-mp="5brcaz6cix32" data-mpi={i}>
                    <Check className="w-4 h-4 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-934" data-mp="dhh4okmfwhg" />
                    <span data-id="element-935" data-mp="3sibjgiqj91ln">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="w-full md:w-72 shrink-0 space-y-4 md:border-l md:border-slate-800 md:pl-10" data-id="element-936" data-mp="1uqfph2mrgj25">
              <div className="flex items-end justify-between" data-id="element-937" data-mp="1qqf2gtllizjh">
                <span className="text-xs text-slate-500 uppercase tracking-wider" data-id="element-938" data-mp="3k7b8gtjliqrr">
                  Build (One-Time)
                </span>
                <span className="text-xl font-bold text-white" data-id="element-939" data-mp="3p15qlsjjyxoq">$1,000*</span>
              </div>
              <p className="text-xs text-slate-500 -mt-2" data-id="element-940" data-mp="3b5l6its389fq">
                *Must be built in conjunction with a standard campaign.
              </p>
              <div className="flex items-end justify-between pt-2 border-t border-slate-800/50" data-id="element-941" data-mp="6t7v7znw66mz">
                <span className="text-xs text-[#7FE0C3] uppercase tracking-wider" data-id="element-942" data-mp="bssvqeipmn1t">
                  Monthly Management
                </span>
                <span className="text-xl font-bold text-white" data-id="element-943" data-mp="3txw5icjmfn8k">Same</span>
              </div>
              <p className="text-xs text-slate-500 -mt-2" data-id="element-944" data-mp="2qbao3esk0ge">
                Follows the standard campaign structure and pricing.
              </p>
              <div className="flex items-end justify-between pt-2 border-t border-slate-800/50" data-id="element-945" data-mp="2nhtwjwu0s8hm">
                <span className="text-xs text-[#7FE0C3] uppercase tracking-wider" data-id="element-946" data-mp="2f0t8ws058uud">
                  Retention bonus
                </span>
                <span className="text-xl font-bold text-white" data-id="element-947" data-mp="2aypubui6ha4v">$2,500</span>
              </div>
              <p className="text-xs text-slate-500 -mt-2" data-id="element-948" data-mp="2oxxwmw9n0yda">
                Per month starting at day 30 active in the role, capped at 6
                payments.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. HOW TO CHOOSE */}
      <section className="px-6 md:px-12 max-w-3xl mx-auto" data-id="element-949" data-mp="1rc0ifb2ajf75">
        <div className="text-center mb-10" data-id="element-950" data-mp="1r9fgyn5wn19x">
          <h2 className="text-3xl font-bold text-white mb-3" data-id="element-951" data-mp="260oav8gub8sv">
            How to scope it
          </h2>
          <p className="text-slate-400" data-id="element-952" data-mp="219eqama5evnu">
            A quick way to land on the right shape for where your team is today.
          </p>
        </div>

        <div className="space-y-4" data-id="element-953" data-mp="1dhr3afg0c4qu">
          <div className="flex items-start bg-slate-900/30 border border-[#7FE0C3]/20 rounded-xl p-5" data-id="element-954" data-mp="18liqh8pa8nmu">
            <FileText className="w-5 h-5 text-[#7FE0C3] mr-4 mt-0.5 shrink-0" data-id="element-955" data-mp="buf1r3b0ifye" />
            <p className="text-slate-300 text-sm leading-relaxed" data-id="element-956" data-mp="gsgukfy8ptn2">
              <span className="text-white font-medium" data-id="element-957" data-mp="gqma12qvs3ji">Start with Advantage</span>{' '}
              sized to the markets you are hiring in. Campaign scope and monthly
              investment track the ad spend you want managed.
            </p>
          </div>
          <div className="flex items-start bg-slate-900/30 border border-[#7FE0C3]/20 rounded-xl p-5" data-id="element-958" data-mp="uj26fkpa13bg">
            <Plus className="w-5 h-5 text-[#7FE0C3] mr-4 mt-0.5 shrink-0" data-id="element-959" data-mp="zi57yv61sreo" />
            <p className="text-slate-300 text-sm leading-relaxed" data-id="element-960" data-mp="14hrr1l8k51ve">
              <span className="text-white font-medium" data-id="element-961" data-mp="14ggmhj7afq38">Add a recruiter</span> if
              interviewing is eating your leadership's time and you want
              execution handled end to end.
            </p>
          </div>
          <div className="flex items-start bg-slate-900/30 border border-[#7FE0C3]/20 rounded-xl p-5" data-id="element-962" data-mp="3aapz33x9c9og">
            <Crown className="w-5 h-5 text-[#7FE0C3] mr-4 mt-0.5 shrink-0" data-id="element-963" data-mp="32s9y0g3cz8jn" />
            <p className="text-slate-300 text-sm leading-relaxed" data-id="element-964" data-mp="2y10e7w4hqzfo">
              <span className="text-white font-medium" data-id="element-965" data-mp="2xzpb7yivdvpm">Add Leadership</span>{' '}
              when you also need to recruit the managers and team leaders who
              will scale your operation.
            </p>
          </div>
        </div>

        <div className="mt-12 text-center" data-id="element-966" data-mp="3lr4go05y0t53">
          <p className="text-slate-500 text-sm max-w-xl mx-auto leading-relaxed" data-id="element-967" data-mp="3lq2up0ne2m8l">
            Most partners start with Advantage as the foundation, then add a
            recruiter or Leadership for management hiring as their needs grow.
          </p>
        </div>
      </section>
    </div>;
}